import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";

const FALLBACK = [
    {
        src: "https://images.unsplash.com/photo-1763889784402-5e8744af31b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxtb29keSUyMG11c2ljaWFuJTIwcG9ydHJhaXQlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzg2ODYzODYwfDA&ixlib=rb-4.1.0&q=85",
        alt: "Backstage portrait",
    },
    {
        src: "https://images.unsplash.com/photo-1478147427282-58a87a120781?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwY3Jvd2QlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzg2ODYzODYwfDA&ixlib=rb-4.1.0&q=85",
        alt: "Crowd in the dark",
    },
    {
        src: "https://images.unsplash.com/photo-1518972559570-7cc1309f3229?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMHN0dWRpbyUyMGVxdWlwbWVudCUyMGRhcmt8ZW58MHx8fHwxNzg2ODYzODcyfDA&ixlib=rb-4.1.0&q=85",
        alt: "Studio mixer at night",
    },
    {
        src: "https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZXh0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODY4NjM4NjB8MA&ixlib=rb-4.1.0&q=85",
        alt: "Dark texture",
    },
    {
        src: "https://images.unsplash.com/photo-1498798821241-1f327af804fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb29keSUyMG11c2ljaWFuJTIwcG9ydHJhaXQlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzg2ODYzODYwfDA&ixlib=rb-4.1.0&q=85",
        alt: "Under the spotlight",
    },
    {
        src: "https://images.unsplash.com/photo-1581084349663-7ab88c58d362?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHx2aW55bCUyMHJlY29yZCUyMGFic3RyYWN0JTIwZGFya3xlbnwwfHx8fDE3ODY4NjM4NzJ8MA&ixlib=rb-4.1.0&q=85",
        alt: "Vinyl close-up",
    },
];

const SPANS = ["row-span-2", "col-span-2", "row-span-2", "", "col-span-2", ""];

export default function InstagramGrid() {
    const [posts, setPosts] = useState(null);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instagram/feed?limit=6`)
            .then((r) => r.json())
            .then((data) => {
                if (data.configured && data.items?.length) setPosts(data.items);
            })
            .catch(() => {});
    }, []);

    const tiles = posts
        ? posts.map((p, i) => ({
              src: p.image_url,
              alt: p.caption ? p.caption.slice(0, 80) : "Instagram post",
              href: p.permalink,
              cls: SPANS[i % SPANS.length],
          }))
        : FALLBACK.map((img, i) => ({ ...img, href: null, cls: SPANS[i] }));

    return (
        <section
            id="social"
            data-testid="instagram-section"
            className="px-6 py-24 md:px-16 md:py-40"
        >
            <div className="mb-16 flex flex-col gap-6 md:mb-24 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
                        03 / Social
                    </p>
                    <h2 className="font-serif text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl">
                        On the <span className="italic text-zinc-400">feed</span>
                    </h2>
                    {posts && (
                        <p
                            data-testid="instagram-live-badge"
                            className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-blood"
                        >
                            Live from Instagram
                        </p>
                    )}
                </div>
                <a
                    data-testid="instagram-handle-link"
                    href="https://www.instagram.com/bejnni"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex w-fit items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-bone"
                >
                    <Instagram className="h-4 w-4 transition-colors duration-300 group-hover:text-blood" />
                    @bejnni
                </a>
            </div>

            <div className="grid auto-rows-[160px] grid-cols-2 gap-3 md:auto-rows-[220px] md:grid-cols-4 md:gap-4">
                {tiles.map((tile, i) => {
                    const inner = (
                        <>
                            <img
                                src={tile.src}
                                alt={tile.alt}
                                className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                            />
                            <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone">
                                    @bejnni
                                </span>
                            </div>
                        </>
                    );
                    return (
                        <motion.div
                            key={tile.href || tile.src + i}
                            data-testid={`instagram-item-${i}`}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 1,
                                delay: (i % 3) * 0.1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className={`group relative overflow-hidden ${tile.cls}`}
                        >
                            {tile.href ? (
                                <a
                                    href={tile.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Open Instagram post"
                                    className="block h-full w-full"
                                >
                                    {inner}
                                </a>
                            ) : (
                                inner
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}
